import { AssetCategory, AssetCategoryJSON } from "./AssetCategory";
import { AssetSubCategory, AssetSubCategoryJSON } from "./AssetSubCategory";
import { AssetImage, AssetImagesJSON } from "./AssetImage";
import {
  AssetDetail,
  AssetDigital,
  AssetFinancial,
  AssetHousehold,
  AssetRealEstate,
  AssetVehicle,
} from "./AssetDetail";

export type AssetJSON = {
  id?: number;
  asset?: string;
  description?: string;
  value?: number;
  images?: AssetImagesJSON[];
  estate_id?: number;
  category_id?: number;
  category?: AssetCategoryJSON;
  subcategory_id?: number;
  subcategory?: AssetSubCategoryJSON;
  is_probate?: boolean | null;
  asset_household?: { quantity?: number };
  asset_financial?: { bank_institution?: string };
  asset_real_estate?: {
    address1?: string;
    address2?: string;
    assessor_parcel_number?: string;
    city?: string;
    states_id?: number;
    zip_code?: string;
    counties_id?: number;
    google_places_id?: string;
    formatted_address?: string;
    countries_id?: number;
    ownership?: string;
    has_mortgage?: string;
    remaining_mortgage?: number;
    title_type?: string;
    title?: string;
    future_plan?: string;
  };
  asset_vehicles?: {
    make?: string;
    model?: string;
    vin?: string;
    year?: number;
  };
  asset_digital?: { web_address?: string };
};

export class Asset {
  constructor(
    public id?: number,
    public asset?: string,
    public description?: string,
    public value?: number | null,
    public images?: AssetImage[],
    public estateId?: number,
    public categoryId?: number,
    public category?: AssetCategory,
    public subcategoryId?: number,
    public subcategory?: AssetSubCategory,
    public isProbate?: boolean,
    public detail?: AssetDetail
  ) {}
  static fromJSON = (json: AssetJSON): Asset => {
    let detail: AssetDetail | undefined = undefined;
    if (json?.category?.category) {
      switch (json.category.category) {
        case "Household":
          detail = json?.asset_household
            ? new AssetHousehold(json?.asset_household?.quantity)
            : undefined;
          break;
        case "Financial":
          detail = json?.asset_financial
            ? new AssetFinancial(json?.asset_financial?.bank_institution)
            : undefined;
          break;
        case "Real Estate":
          detail = json?.asset_real_estate
            ? new AssetRealEstate(
                json?.asset_real_estate?.address1,
                json?.asset_real_estate?.address2,
                json?.asset_real_estate?.assessor_parcel_number,
                json?.asset_real_estate?.city,
                json?.asset_real_estate?.states_id,
                json?.asset_real_estate?.zip_code,
                json?.asset_real_estate?.counties_id,
                json?.asset_real_estate?.google_places_id,
                json?.asset_real_estate?.formatted_address,
                json?.asset_real_estate?.countries_id,
                json?.asset_real_estate?.ownership,
                json?.asset_real_estate?.has_mortgage,
                json?.asset_real_estate?.remaining_mortgage,
                json?.asset_real_estate?.title_type,
                json?.asset_real_estate?.title,
                json?.asset_real_estate?.future_plan
              )
            : undefined;
          break;
        case "Vehicles":
          detail = json?.asset_vehicles
            ? new AssetVehicle(
                json?.asset_vehicles?.make,
                json?.asset_vehicles?.model,
                json?.asset_vehicles?.year,
                json?.asset_vehicles?.vin
              )
            : undefined;
          break;
        case "Digital":
          detail = json?.asset_digital
            ? new AssetDigital(json?.asset_digital?.web_address)
            : undefined;
          break;
      }
    }

    return new Asset(
      json.id,
      json.asset,
      json.description,
      json.value,
      json.images?.map((imageJson: any, index: number) =>
        AssetImage.fromJSON(imageJson, index, json.estate_id)
      ),
      json.estate_id,
      json.category_id,
      json.category ? AssetCategory.fromJSON(json.category) : undefined,
      json.subcategory_id,
      json.subcategory
        ? AssetSubCategory.fromJSON(json.subcategory)
        : undefined,
      json.is_probate || undefined,
      detail
    );
  };

  toJson(): AssetJSON {
    const json: any = {};
    json.assets_id = this.id;
    this.asset ? (json.asset = this.asset) : null;
    this.description ? (json.description = this.description) : null;
    this.value ? (json.value = this.value) : null;
    this.estateId ? (json.estate_id = this.estateId) : null;
    this.category?.id ? (json.categories_id = this.category?.id) : null;
    this.subcategory?.id
      ? (json.subcategories_id = this.subcategory?.id)
      : null;
    this.isProbate !== undefined ? (json.is_probate = this.isProbate) : null;
    // combine the detail properties into a single object
    return { ...json, ...this?.detail?.toJson() };
  }
}
